import { RotateCcw, Search } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../../components/Spinner";
import { apiRequest } from "../../../lib/api";
import { formatTimestamp } from "../../../lib/format";
import { useInvalidateAll, useKuspitNotifications } from "../../../lib/queries";
import type { KuspitNotification, KuspitNotificationStatus } from "../../../types";
import { EmptyRow, Feedback, KUSPIT_EVENT_LABELS, PageError, Pill, describeKuspitEvent, useAction } from "../shared";
import { KuspitFrame } from "./KuspitShared";

const STATUSES: Array<{ value: KuspitNotificationStatus | ""; label: string }> = [
  { value: "", label: "Todas" }, { value: "failed", label: "Fallidas" }, { value: "received", label: "En cola" }, { value: "processed", label: "Aplicadas" }, { value: "ignored", label: "Ignoradas" }
];
const isStatus = (value: string | null): value is KuspitNotificationStatus => value === "failed" || value === "received" || value === "processed" || value === "ignored";

/** What Kuspit told us and when, and whether the worker managed to apply it. */
export function AdminKuspitWebhooksPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const status = isStatus(params.get("estatus")) ? params.get("estatus") as KuspitNotificationStatus : "";
  const event = params.get("evento") ?? "";
  const notifications = useKuspitNotifications({ status, event: event || undefined, q: search.trim() || undefined });
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };
  return (
    <KuspitFrame title="Webhooks de Kuspit" subtitle="Depósitos, contratos y transferencias que Kuspit notifica de forma asíncrona.">
      <div className="discover-toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Identificador, CLABE, clave de rastreo o cliente" aria-label="Buscar notificaciones" />
        </label>
        <div className="chip-row" role="tablist" aria-label="Filtrar por estatus">
          {STATUSES.map((item) => (
            <button key={item.value || "all"} type="button" role="tab" aria-selected={status === item.value} className={`chip${status === item.value ? " active" : ""}`} onClick={() => update("estatus", item.value)}>{item.label}</button>
          ))}
        </div>
        <select className="standalone-input small" value={event} onChange={(e) => update("evento", e.target.value)} aria-label="Filtrar por evento">
          <option value="">Todos los eventos</option>
          {Object.entries(KUSPIT_EVENT_LABELS).map(([code, label]) => <option key={code} value={code}>{code} · {label}</option>)}
        </select>
        <span className="toolbar-count">{notifications.data?.length ?? 0} notificaciones</span>
      </div>
      {notifications.isLoading && <div className="page-state"><Spinner label="Cargando notificaciones" /></div>}
      {notifications.isError && <PageError title="No pudimos cargar las notificaciones" retry={() => void notifications.refetch()} />}
      {notifications.data && (
        <div className="table-wrap">
          <table className="admin-table">
            <thead><tr><th>Recibida</th><th>Evento</th><th>Estatus</th><th>Cliente</th><th>Detalle</th><th>Acciones</th></tr></thead>
            <tbody>
              {notifications.data.map((row) => <NotificationRow key={row.id} row={row} />)}
              {notifications.data.length === 0 && <EmptyRow colSpan={6}>Sin notificaciones{status || event ? " con este filtro" : ""}.</EmptyRow>}
            </tbody>
          </table>
        </div>
      )}
    </KuspitFrame>
  );
}

function summarize(payload: Record<string, unknown>): string {
  const data = (payload.data && typeof payload.data === "object" ? payload.data : payload) as Record<string, unknown>;
  const parts: string[] = [];
  if (data.monto !== undefined) parts.push(`$${data.monto}`);
  if (data.cuentaBeneficiaria) parts.push(`CLABE ${data.cuentaBeneficiaria}`);
  if (data.contrato) parts.push(`contrato ${data.contrato}`);
  if (data.idExterno) parts.push(`idExterno ${data.idExterno}`);
  if (data.claveRastreo) parts.push(`rastreo ${data.claveRastreo}`);
  return parts.join(" · ");
}

function NotificationRow({ row }: { row: KuspitNotification }) {
  const [open, setOpen] = useState(false);
  const action = useAction();
  const invalidate = useInvalidateAll();
  const retry = () => action.run("retry", async () => {
    await apiRequest(`/api/v1/admin/kuspit/notifications/${row.id}/retry`, { method: "POST" });
    await invalidate();
    return "Reencolada. El worker la aplicará en su siguiente ciclo (menos de un minuto).";
  });
  return (
    <>
      <tr className={row.status === "failed" ? "row-bad" : undefined}>
        <td><div className="table-primary"><strong>{formatTimestamp(row.receivedAt)}</strong><span className="mono" translate="no">{row.notificationId}</span></div></td>
        <td>{describeKuspitEvent(row.eventCode)}</td>
        <td><div className="table-primary"><span><Pill status={row.status} /></span><span>{row.attempts} intento(s){row.processedAt ? ` · ${formatTimestamp(row.processedAt)}` : ""}</span></div></td>
        <td>{row.owner ? <Link to={`/admin/usuarios/${row.owner.id}`} className="table-primary"><strong>{row.owner.fullName}</strong><span>{row.owner.email}</span></Link> : <span className="muted">Sin atribuir</span>}</td>
        <td><div className="table-primary"><span translate="no">{summarize(row.payload) || "—"}</span>{row.lastError && <span className="error-text">{row.lastError}</span>}</div></td>
        <td>
          <div className="row-actions">
            <button type="button" className="link-button" onClick={() => setOpen((value) => !value)}>{open ? "Ocultar payload" : "Ver payload"}</button>
            {(row.status === "failed" || row.status === "ignored") && (
              <button type="button" className="secondary-button small" onClick={() => void retry()} disabled={action.busy !== null} title="Volver a poner en la cola del worker">
                {action.busy === "retry" ? <Spinner /> : <RotateCcw size={13} aria-hidden="true" />} Reintentar
              </button>
            )}
          </div>
        </td>
      </tr>
      {(open || action.error || action.notice) && (
        <tr className="row-feedback">
          <td colSpan={6}>
            <Feedback error={action.error} notice={action.notice} />
            {open && <pre className="payload" translate="no">{JSON.stringify(row.payload, null, 2)}</pre>}
            {open && <span className="field-hint">Folio de confirmación {row.folioConfirmacion} · correlación {row.correlationId}{row.ledgerEntryId ? ` · asiento ${row.ledgerEntryId}` : ""}</span>}
          </td>
        </tr>
      )}
    </>
  );
}
